import axios from "axios";
import API_URLS from "../../../config/global_variables";
import { labelForId, currencyForId } from "./stores";

const authToken = () => {
  try {
    return JSON.parse(localStorage.userData || "{}").token || "";
  } catch {
    return "";
  }
};

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: authToken(),
});

const ERROR_MESSAGES = {
  VALIDATION_ERROR: "Invalid filter — please check your inputs.",
  NOT_FOUND: "Unknown store.",
  SERVER_ERROR: "Something went wrong. Please try again.",
};

const mapError = (err) => {
  const apiErr = err?.response?.data?.error;
  const code = apiErr?.code;
  const status = err?.response?.status;
  return {
    code: code || "SERVER_ERROR",
    status,
    message: ERROR_MESSAGES[code] || apiErr?.message || ERROR_MESSAGES.SERVER_ERROR,
    details: apiErr?.details || [],
  };
};

const toUrl = (v) => {
  if (!v) return null;
  if (typeof v === "string") return v.trim().replace(/^["']|["']$/g, "") || null;
  if (typeof v === "object") return v.url || v.src || v.image_url || null;
  return null;
};

export const parseImages = (raw) => {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.map(toUrl).filter(Boolean);
  }

  if (typeof raw === "object") {
    const direct = toUrl(raw);
    if (direct) return [direct];
    return [];
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.map(toUrl).filter(Boolean);
        const direct = toUrl(parsed);
        return direct ? [direct] : [];
      } catch {
        // fall through to other heuristics
      }
    }

    if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("/")) {
      return [trimmed];
    }

    if (trimmed.includes(",")) {
      return trimmed
        .split(",")
        .map((s) => toUrl(s))
        .filter(Boolean);
    }
  }

  return [];
};

const formatPrice = (val, symbol) => {
  if (val === null || val === undefined || val === "") return `${symbol}0`;
  const n = parseFloat(val);
  if (Number.isNaN(n)) return `${symbol}0`;
  return `${symbol}${n.toFixed(2)}`;
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB");
};

export const mapProduct = (item) => {
  const symbol = currencyForId(item.store_id);
  return {
    key: item.id,
    id: item.id,
    raw: item,
    sapSku: "",
    sapName: item.prod_name || "",
    sapCustomerNumber: "",
    sapPrice: `${symbol}0`,
    sapMsrp: `${symbol}0`,
    availableStock: 0,
    reservedStock: 0,
    bcSku: item.sku || "NA",
    type: item.bc_product_type || "NA",
    active: !!item.is_purchase,
    havingVariant: !!item.having_variant,
    prodId: item.prod_id ?? null,
    variantId: item.variant_id ?? null,
    store: labelForId(item.store_id),
    bcInventory: item.inventory_tracking === "none" ? "NA" : Number(item.inventory_level) || 0,
    subscription: item.is_subscription ? "YES" : "NO",
    enrichment: "",
    lastUpdated: formatDateTime(item.updated_at),
    bcUrl: item.bc_url || "",
    prodImg: item.prod_img || "",
    salePriceDisplay: formatPrice(item.prod_sale_price, symbol),
    msrpDisplay: formatPrice(item.prod_msrp, symbol),
    isDefault: Number(item.is_default) === 1 ? 1 : 0,
    tagCount: Number(item.tag_count) || 0,
    // bucket assignment info (may be present in API response)
    assignedProdTypeId: item.prod_type_id ?? item.productTypeId ?? null,
    assignedProdTypeName: item.prod_type ?? item.prodType ?? null,
    assignedBucketId: item.bucket_id ?? item.bucketId ?? null,
    assignedBucketKey: item.bucket_key ?? item.bucketKey ?? null,
    assignedBucketName: item.bucket_name ?? item.bucketName ?? item.bucket_display_name ?? null,
  };
};

const fetchProducts = async (filters, { signal } = {}) => {
  const params = { store_label: filters.store_label };
  if (filters.search) params.search = filters.search;
  if (filters.bucket_id) params.bucket_id = filters.bucket_id;
  if (filters.default_only) params.is_default = 1;
  if (filters.prod_type_id) params.prod_type_id = filters.prod_type_id;
  if (filters.is_active === 0 || filters.is_active === 1) params.is_active = filters.is_active;
  if (filters.has_bucket === 0 || filters.has_bucket === 1) params.has_bucket = filters.has_bucket;
  if (filters.tag_filter && filters.tag_filter !== "all") params.tag_filter = filters.tag_filter;
  if (filters.has_tag) params.has_tag = filters.has_tag;
  params.page = filters.page || 1;
  params.limit = filters.limit || 20;

  const res = await axios.get(API_URLS.GET_NTS_PRODUCTS, { headers: headers(), params, signal });
  const payload = res?.data?.data || {};
  return {
    items: (payload.items || []).map(mapProduct),
    pagination: payload.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
  };
};

const fetchProductById = async (id) => {
  const res = await axios.get(`${API_URLS.GET_NTS_PRODUCT_BY_ID}/${id}`, { headers: headers() });
  return res?.data?.data || null;
};

const toggleProductActive = async (record, checked) => {
  const isVariant = !!record.havingVariant;
  const payload = {
    store: record.store,
    product_id: record.prodId,
    status: checked,
    is_variant: isVariant,
  };
  if (isVariant) payload.variant_id = record.variantId;

  const res = await axios.post(API_URLS.UPDATE_BC_PRODUCT_STATUS, payload, {
    headers: headers(),
  });
  return res?.data;
};

const apiHelpers = {
  fetchProducts,
  fetchProductById,
  toggleProductActive,
  parseImages,
  mapError,
};

export default apiHelpers;
